import { parseTaskAttention } from "../persistence/records"
import { isProjectColor, parseProjectChars, normalizeProjectEmoji } from "../projectAppearance"
import type { ComputerOverview, ComputerOverviewProject, RecentComputerTask } from "./types"

export const OVERVIEW_CACHE_KEY = "wildlark:computer-overviews:v1"

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function parseProject(value: unknown): ComputerOverviewProject | null {
    if (!isRecord(value) || typeof value.id !== "string" || typeof value.name !== "string") return null
    return {
        id: value.id,
        name: value.name,
        ...(value.archived === true ? { archived: true } : {}),
        ...(isProjectColor(value.color) ? { color: value.color } : {}),
        ...(typeof value.glyph === "string" && value.glyph ? { glyph: value.glyph } : {}),
        ...(parseProjectChars(value.projectChars) ? { projectChars: parseProjectChars(value.projectChars) } : {}),
        ...(typeof value.projectEmoji === "string" && normalizeProjectEmoji(value.projectEmoji)
            ? { projectEmoji: normalizeProjectEmoji(value.projectEmoji) }
            : {}),
    }
}

function parseTask(value: unknown): RecentComputerTask | null {
    if (!isRecord(value)) return null
    if (
        typeof value.repoName !== "string" ||
        typeof value.taskId !== "string" ||
        typeof value.title !== "string" ||
        typeof value.lastActivityAt !== "string" ||
        typeof value.workspaceId !== "string"
    ) {
        return null
    }
    return {
        attention: parseTaskAttention(value.attention),
        ...(typeof value.sleeping === "boolean" ? { sleeping: value.sleeping } : {}),
        repoName: value.repoName,
        taskId: value.taskId,
        title: value.title,
        lastActivityAt: value.lastActivityAt,
        running: value.running === true,
        unread: value.unread === true,
        workspaceId: value.workspaceId,
    }
}

function count(value: unknown): number {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : 0
}

/**
 * A cached overview is last-known state: its projects and tasks render
 * immediately at launch, but nothing about reachability is trusted until a
 * fresh read replaces it, so status always comes back as "unknown".
 */
export function parseCachedOverview(value: unknown): ComputerOverview | null {
    if (!isRecord(value) || typeof value.nodeId !== "string" || typeof value.refreshedAt !== "string") return null
    const projects = Array.isArray(value.projects)
        ? value.projects.map(parseProject).filter((project): project is ComputerOverviewProject => project !== null)
        : []
    const recentTasks = Array.isArray(value.recentTasks) ? value.recentTasks.map(parseTask).filter((task): task is RecentComputerTask => task !== null) : []
    return {
        nodeId: value.nodeId,
        status: "unknown",
        needsAttentionCount: count(value.needsAttentionCount),
        openTaskCount: count(value.openTaskCount),
        runningCount: 0,
        workspaceCount: count(value.workspaceCount),
        projects,
        recentTasks: recentTasks.map((task) => ({ ...task, running: false })),
        refreshedAt: value.refreshedAt,
    }
}

export function readOverviewCache(storage: Pick<Storage, "getItem"> = window.localStorage): Record<string, ComputerOverview> {
    try {
        const raw = storage.getItem(OVERVIEW_CACHE_KEY)
        if (!raw) return {}
        const value: unknown = JSON.parse(raw)
        if (!isRecord(value) || value.v !== 1 || !isRecord(value.overviews)) return {}
        const overviews: Record<string, ComputerOverview> = {}
        for (const [nodeId, entry] of Object.entries(value.overviews)) {
            const parsed = parseCachedOverview(entry)
            if (parsed && parsed.nodeId === nodeId) overviews[nodeId] = parsed
        }
        return overviews
    } catch {
        return {}
    }
}

export function writeOverviewCache(overviews: Readonly<Record<string, ComputerOverview>>, storage: Pick<Storage, "setItem"> = window.localStorage): void {
    try {
        const entries = Object.values(overviews)
            .filter((overview) => overview.status !== "unknown" || overview.projects.length > 0)
            .map((overview) => [
                overview.nodeId,
                {
                    nodeId: overview.nodeId,
                    needsAttentionCount: overview.needsAttentionCount,
                    openTaskCount: overview.openTaskCount,
                    workspaceCount: overview.workspaceCount,
                    projects: overview.projects,
                    recentTasks: overview.recentTasks,
                    refreshedAt: overview.refreshedAt,
                },
            ])
        storage.setItem(OVERVIEW_CACHE_KEY, JSON.stringify({ v: 1, overviews: Object.fromEntries(entries) }))
    } catch {
        // The cache only speeds up the first paint; losing it costs one refresh.
    }
}
